import * as React from "react";
import { CheckCircle2, Loader2, AlertCircle, XCircle, ChevronDown, ChevronUp } from "lucide-react";
import type { ListingQualityResult } from "@/app/api/auth-client";
import {
  listingQualityBarColorClass,
  listingQualityTierColorClass,
  listingQualityTierLabel,
} from "@/app/utils/listing-quality-utils";

export interface ListingQualityPanelProps {
  result: ListingQualityResult | null;
  loading?: boolean;
  error?: string | null;
  /** Compact: score bar only, no checklist */
  compact?: boolean;
  /** Inline row for ad cards (smaller score, horizontal layout) */
  inline?: boolean;
  /** Show expandable checklist (for my-ads card) */
  collapsibleChecklist?: boolean;
  defaultChecklistOpen?: boolean;
  checklistOpen?: boolean;
  onChecklistOpenChange?: (open: boolean) => void;
  /** Override headline score/tier from persisted ad fields */
  persistedScore?: number;
  persistedTier?: string;
  pendingQuality?: boolean;
}

export function ListingQualityPanel({
  result,
  loading = false,
  error = null,
  compact = false,
  inline = false,
  collapsibleChecklist = false,
  defaultChecklistOpen = false,
  checklistOpen: checklistOpenProp,
  onChecklistOpenChange,
  persistedScore,
  persistedTier,
  pendingQuality = false,
}: ListingQualityPanelProps) {
  const [checklistOpenInternal, setChecklistOpenInternal] = React.useState(defaultChecklistOpen);
  const checklistOpen = checklistOpenProp ?? checklistOpenInternal;
  const setChecklistOpen = onChecklistOpenChange ?? setChecklistOpenInternal;

  const score = result?.score ?? persistedScore ?? 0;
  const tier = result?.tier ?? persistedTier;
  const tierLabel = listingQualityTierLabel(tier);
  const discoveryEligible = result?.discovery_eligible;
  const issues = result?.issues ?? [];
  const showChecklist =
    (!compact || (collapsibleChecklist && checklistOpen)) &&
    (issues.length > 0 || loading || result != null);

  const scoreClass = inline ? "text-sm font-semibold tabular-nums" : compact ? "text-base font-semibold tabular-nums" : "text-lg font-semibold tabular-nums";
  const barClass = inline ? "h-1 w-14 sm:w-20" : compact ? "h-1.5 w-full" : "h-2 w-full";

  const checklistBlock = showChecklist && (!collapsibleChecklist || checklistOpen) && (
    <ul className={`space-y-1.5 ${inline ? "pt-2" : "pt-1 border-t border-border/60"}`}>
      {loading && issues.length === 0 && (
        <li className="text-xs text-muted-foreground">Calculando…</li>
      )}
      {!loading && issues.length === 0 && result && (
        <li className="text-xs text-green-700 flex items-center gap-1.5">
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
          Anúncio completo para os critérios atuais
        </li>
      )}
      {issues.map((issue) => (
        <li
          key={issue.code}
          className={`text-xs flex items-start gap-1.5 ${issue.knockout ? "text-red-700 font-medium" : "text-muted-foreground"}`}
        >
          <XCircle className="h-3.5 w-3.5 shrink-0 mt-0.5 text-red-500" aria-hidden />
          <span>{issue.message}</span>
        </li>
      ))}
    </ul>
  );

  if (inline) {
    return (
      <div className="min-w-0 space-y-1.5">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-[11px] font-medium text-muted-foreground">Qualidade</span>
          {pendingQuality && !result && !loading ? (
            <span className="text-xs text-muted-foreground">Pendente</span>
          ) : (
            <>
              {loading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" aria-hidden />
              ) : (
                <>
                  <div
                    className={`${barClass} shrink-0 rounded-full bg-muted overflow-hidden`}
                    role="progressbar"
                    aria-valuenow={score}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  >
                    <div
                      className={`h-full rounded-full transition-all ${listingQualityBarColorClass(tier)}`}
                      style={{ width: `${Math.min(100, Math.max(0, score))}%` }}
                    />
                  </div>
                  <span className={scoreClass}>{score}/100</span>
                  {tier && (
                    <span
                      className={`inline-flex px-1.5 py-0 rounded-full text-[10px] font-medium border ${listingQualityTierColorClass(tier)}`}
                    >
                      {tierLabel}
                    </span>
                  )}
                </>
              )}
            </>
          )}
          {collapsibleChecklist && showChecklist && (
            <button
              type="button"
              onClick={() => setChecklistOpen(!checklistOpen)}
              className="shrink-0 p-0.5 rounded text-muted-foreground hover:text-foreground"
              aria-expanded={checklistOpen}
              aria-label={checklistOpen ? "Ocultar checklist" : "Ver como melhorar"}
            >
              {checklistOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>
          )}
        </div>
        {error && (
          <p className="text-xs text-destructive flex items-center gap-1">
            <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />
            {error}
          </p>
        )}
        {checklistBlock}
      </div>
    );
  }

  return (
    <div className={`rounded-lg border border-border bg-muted/30 space-y-2 ${compact ? "p-2" : "p-3"}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className={`font-medium text-muted-foreground uppercase tracking-wide ${compact ? "text-[10px]" : "text-xs"}`}>
            Qualidade do anúncio
          </p>
          {pendingQuality && !result && !loading ? (
            <p className="text-sm text-muted-foreground mt-1">Qualidade pendente — abra detalhes para calcular</p>
          ) : (
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden />
              ) : (
                <span className={scoreClass}>{score}/100</span>
              )}
              {!loading && tier && (
                <span
                  className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${listingQualityTierColorClass(tier)}`}
                >
                  {tierLabel}
                </span>
              )}
              {!loading && !compact && discoveryEligible != null && (
                <span
                  className={`text-xs ${discoveryEligible ? "text-green-700" : "text-amber-800"}`}
                >
                  {discoveryEligible ? "Aparece na busca" : "Não aparece na busca"}
                </span>
              )}
            </div>
          )}
        </div>
        {collapsibleChecklist && showChecklist && (
          <button
            type="button"
            onClick={() => setChecklistOpen(!checklistOpen)}
            className="shrink-0 p-1 rounded text-muted-foreground hover:text-foreground"
            aria-expanded={checklistOpen}
            aria-label={checklistOpen ? "Ocultar checklist" : "Ver como melhorar"}
          >
            {checklistOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        )}
      </div>

      {!pendingQuality && !loading && (
        <div className={`${barClass} rounded-full bg-muted overflow-hidden`} role="progressbar" aria-valuenow={score} aria-valuemin={0} aria-valuemax={100}>
          <div
            className={`h-full rounded-full transition-all ${listingQualityBarColorClass(tier)}`}
            style={{ width: `${Math.min(100, Math.max(0, score))}%` }}
          />
        </div>
      )}

      {error && (
        <p className="text-xs text-destructive flex items-center gap-1">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {error}
        </p>
      )}

      {checklistBlock}

      {!compact && result?.min_score_discovery != null && (
        <p className="text-[10px] text-muted-foreground">
          Mín. busca: {result.min_score_discovery} · Mín. publicar: {result.min_score_publish ?? "—"}
        </p>
      )}
    </div>
  );
}
